import { isPresent } from '@jjvgaming/player-payback-library'
import { object } from 'yup'
import { parseISO, formatISO, parse } from 'date-fns'
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { type ElementType, type FormEvent, useEffect, useState } from 'react'

import { useDebounce } from '@uidotdev/usehooks'
import { useQuery } from '@tanstack/react-query'
import { SearchField } from 'components/SearchField'
import { colorPrimitives } from 'components/Theme'
import { colors } from 'components/ThemeProvider'
import RewardProgramIcon from 'assets/nav_icons/rewards_program.svg?react'
import CloseIcon from 'assets/close-filled.svg?react'
import { useGetRewardProgramById } from 'hooks/api/useGetRewardProgramById'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { ActivityButton } from 'components/ActivityButton'
import { Modal } from 'components/Modal/Modal'
import { useLayout } from 'hooks/useLayout'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import {
  badgeRewardNameSchema,
  badgeRewardDescriptionSchema,
  locationBadgeTypeSchema,
  badgeRewardSubtypeSchema,
  badgeRewardRenewableTypeSchema,
  editBadgeRewardStartDateSchema,
  editBadgeRewardStartTimeSchema,
  editBadgeRewardEndDateSchema,
  editBadgeRewardEndTimeSchema,
  badgeRewardDependsOnBadgeRewardIdSchema,
  badgeRewardSchedulesSchema,
  badgeRewardJJPointsAwardedSchema,
  badgeRewardCountSchema,
  badgeRewardLicensedEstablishmentIdsSchema,
  badgeRewardCorporateAccountIdSchema,
  badgeRewardOrganizationIdSchema,
  badgeRewardsProgramId,
  badgeRewardLimitCheckinsSchema,
} from 'utils/schemas/BadgeRewardSchema'
import { useSnackbar } from 'stores/useSnackbar'
import { usePutBadgeReward } from 'hooks/api/Contests/BadgeReward/usePutBadgeReward'
import {
  type BadgeRewardSchedule,
  type BadgeRewardRenewableType,
  type BadgeRewardType,
  type BadgeSubtype,
  type BadgeReward,
  type RewardsProgram,
} from 'types/api'
import { ScheduleRow } from 'pages/ClientManagement/LicensedEstablishments/LicensedEstablishmentNewPage/ScheduleRow'
import { addMissingDays } from 'pages/RewardCatalogs/RewardsCatalogsUtils'
import { Counter } from 'components/Counter/Counter'
import {
  badgeRewardTypeHasAccessToControl,
  getBadgeRewardTypeName,
} from 'utils/badgeRewardsUtils'
import { ContestSubtypeSelect as LocationBadgeTypeSelect } from 'components/Shared/ContestSubtypeSelect'
import { LocationSelect } from 'components/Shared/LocationSelect'
import { SelectCorporateAccount } from 'components/SelectCorporateAccount'
import { SelectFromAllOrganizations } from 'components/SelectFromAllOrganizations'
import { useGetAllLicensedEstablishments } from 'hooks/api/LicensedEstablishment/useGetAllLicensedEstablishments'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'
import { type ApiError } from 'openapi-typescript-fetch'
import { useLocationScopedSweepstakes } from 'hooks/api/Contests/BadgeReward/useLocationScopedSweepstakes'

const resolveComponent = (componentSource: unknown): ElementType => {
  if (
    componentSource &&
    typeof componentSource === 'object' &&
    'default' in componentSource
  ) {
    return (componentSource as { default: ElementType }).default
  }

  return componentSource as ElementType
}

export const BadgeRewardSchema = object().shape({
  name: badgeRewardNameSchema,
  description: badgeRewardDescriptionSchema,
  type: locationBadgeTypeSchema,
  subtype: badgeRewardSubtypeSchema,
  renewable: badgeRewardRenewableTypeSchema,
  startDate: editBadgeRewardStartDateSchema.required('Must enter a start date'),
  startTime: editBadgeRewardStartTimeSchema.required('Must enter a start time'),
  endDate: editBadgeRewardEndDateSchema,
  endTime: editBadgeRewardEndTimeSchema,
  dependsOnBadgeRewardId: badgeRewardDependsOnBadgeRewardIdSchema,
  jjPointsAwarded: badgeRewardJJPointsAwardedSchema,
  count: badgeRewardCountSchema,
  limitCheckins: badgeRewardLimitCheckinsSchema,
  schedules: badgeRewardSchedulesSchema,
  licensedEstablishmentIds: badgeRewardLicensedEstablishmentIdsSchema,
  corporateAccountId: badgeRewardCorporateAccountIdSchema,
  organizationId: badgeRewardOrganizationIdSchema,
  rewardsProgramId: badgeRewardsProgramId,
})

const defaultRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
]

const checkInStreakRenewableOptions: BadgeRewardRenewableType[] = [
  'No',
  'LocationCheckInStreak',
]

const getRenewableOptions = (
  type: BadgeRewardType
): BadgeRewardRenewableType[] =>
  type === 'LocationCheckInStreak'
    ? checkInStreakRenewableOptions
    : defaultRenewableOptions

const getValidRenewableValue = (
  type: BadgeRewardType,
  renewableType?: BadgeRewardRenewableType | null
): BadgeRewardRenewableType => {
  const renewableOptions = getRenewableOptions(type)

  return renewableType && renewableOptions.includes(renewableType)
    ? renewableType
    : 'No'
}

export const LocationBadgesEdit = ({
  badgeReward,
  isModalOpen,
  toggleModalIsOpen,
  badgeRewards = {},
}: {
  badgeReward: BadgeReward
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  badgeRewards?: Record<number, string>
}) => {
  const ModalComponent = resolveComponent(Modal)
  const ModalFullHeaderComponent = resolveComponent(ModalFullHeader)
  const ModalFullBodyComponent = resolveComponent(ModalFullBody)
  const ModalFullFooterComponent = resolveComponent(ModalFullFooter)
  const FormFieldErrorMessageComponent = resolveComponent(FormFieldErrorMessage)
  const HookableDateFieldComponent = resolveComponent(HookableDateField)
  const HookableTimeFieldComponent = resolveComponent(HookableTimeField)
  const ActivityButtonComponent = resolveComponent(ActivityButton)
  const ScheduleRowComponent = resolveComponent(ScheduleRow)
  const EventIconComponent = resolveComponent(EventIcon)
  const AccessTimeIconComponent = resolveComponent(AccessTimeIcon)
  const RewardProgramIconComponent = resolveComponent(RewardProgramIcon)
  const CloseIconComponent = resolveComponent(CloseIcon)

  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const { currentStateId } = useCurrentStateStore()
  const { data: allLicensedEstablishments } = useGetAllLicensedEstablishments({
    currentStateId: currentStateId ?? 0,
  })
  const defaultSelectedLocations = (allLicensedEstablishments ?? []).filter(
    (le) => badgeReward.licensedEstablishmentIds?.includes(le.id ?? -1)
  )
  const [locationPointsAwarded, setLocationPointsAwarded] = useState(
    badgeReward.locationPointsAwarded ?? 0
  )
  const [
    locationSweepstakeEntriesAwarded,
    setLocationSweepstakeEntriesAwarded,
  ] = useState(badgeReward.locationSweepstakeEntriesAwarded ?? 0)

  const [count, setCount] = useState(badgeReward.count ?? 0)
  const [countValidationError, setCountValidationError] = useState<
    string | null
  >(null)
  const [
    selectedLocationSweepstakeContestId,
    setSelectedLocationSweepstakeContestId,
  ] = useState<number>(badgeReward.locationSweepstakeContestId ?? 0)
  const [
    selectedLocationSweepstakeGroupId,
    setSelectedLocationSweepstakeGroupId,
  ] = useState<string>(badgeReward.locationSweepstakeGroupId ?? '')
  const [
    locationSweepstakeSelectionError,
    setLocationSweepstakeSelectionError,
  ] = useState<string | null>(null)
  const [rewardProgramSelectionError, setRewardProgramSelectionError] =
    useState<string | null>(null)

  const [selectedBadgeRewardType, setSelectedBadgeRewardType] =
    useState<BadgeRewardType>(badgeReward.type)

  useEffect(() => {
    setSelectedBadgeRewardType(badgeReward.type)
  }, [badgeReward.type])

  const [selectedBadgeRewardRenewable, setSelectedBadgeRewardRenewable] =
    useState<BadgeRewardRenewableType>(
      getValidRenewableValue(badgeReward.type, badgeReward.renewableType)
    )

  const [selectedBadgeRewardDependent, setselectedBadgeRewardDependent] =
    useState<number>(badgeReward.dependentBadgeRewardId ?? 0)

  const formMethods = useForm({
    resolver: yupResolver(BadgeRewardSchema),
    defaultValues: {
      subtype: badgeReward.subtype,
      licensedEstablishmentIds: badgeReward.licensedEstablishmentIds ?? [],
      corporateAccountId: badgeReward.corporateAccountId ?? 0,
      organizationId: badgeReward.organizationId ?? 0,
      rewardsProgramId: badgeReward.rewardsProgramId ?? 0,
    },
  })

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = formMethods

  const subtype = useWatch({ control, name: 'subtype' })
  const selectedLicensedEstablishmentIdsField = useWatch({
    control,
    name: 'licensedEstablishmentIds',
  })
  const selectedCorporateAccountId = useWatch({
    control,
    name: 'corporateAccountId',
  })
  const selectedOrganizationId = useWatch({ control, name: 'organizationId' })

  // Fetch location-scoped sweepstakes based on the selected licensed establishments, corporate account, and organization
  const { regularLocationSweepstakes, groupedLocationSweepstakes } =
    useLocationScopedSweepstakes({
      subtype,
      licensedEstablishmentIds: selectedLicensedEstablishmentIdsField,
      corporateAccountId: selectedCorporateAccountId,
      organizationId: selectedOrganizationId,
    })

  useEffect(() => {
    if (locationSweepstakeEntriesAwarded <= 0) {
      setSelectedLocationSweepstakeContestId(0)
      setSelectedLocationSweepstakeGroupId('')
      setLocationSweepstakeSelectionError(null)
    }
  }, [locationSweepstakeEntriesAwarded])

  useEffect(() => {
    if (
      selectedLocationSweepstakeContestId > 0 &&
      !regularLocationSweepstakes.some(
        (sweepstake) =>
          Number(sweepstake.id) === selectedLocationSweepstakeContestId
      )
    ) {
      setSelectedLocationSweepstakeContestId(0)
    }

    if (
      selectedLocationSweepstakeGroupId.length > 0 &&
      !groupedLocationSweepstakes.some(
        (sweepstake) => sweepstake.groupId === selectedLocationSweepstakeGroupId
      )
    ) {
      setSelectedLocationSweepstakeGroupId('')
    }
  }, [
    regularLocationSweepstakes,
    groupedLocationSweepstakes,
    selectedLocationSweepstakeContestId,
    selectedLocationSweepstakeGroupId,
  ])
  const [rewardProgramSearchQuery, setRewardProgramSearchQuery] = useState('')
  const [selectedRewardProgram, setSelectedRewardProgram] =
    useState<RewardsProgram>()
  const [hasInitializedRewardProgram, setHasInitializedRewardProgram] =
    useState(false)
  const debouncedRewardProgramSearchQuery = useDebounce(
    rewardProgramSearchQuery,
    500
  )
  const [limitCheckins, setLimitCheckins] = useState(
    badgeReward.limitCheckins ?? false
  )

  const existingRewardProgramQuery = useGetRewardProgramById(
    badgeReward.rewardsProgramId ?? 0
  )

  useEffect(() => {
    if (
      !hasInitializedRewardProgram &&
      badgeReward.rewardsProgramId &&
      existingRewardProgramQuery.data
    ) {
      setSelectedRewardProgram(existingRewardProgramQuery.data)
      setHasInitializedRewardProgram(true)
    }
  }, [
    hasInitializedRewardProgram,
    badgeReward.rewardsProgramId,
    existingRewardProgramQuery.data,
  ])

  useEffect(() => {
    if (locationPointsAwarded <= 0) {
      setRewardProgramSearchQuery('')
      setSelectedRewardProgram(undefined)
      setValue('rewardsProgramId', null, { shouldDirty: true })
      setRewardProgramSelectionError(null)
    }
  }, [locationPointsAwarded, setValue])

  const daysToDisplay = addMissingDays(badgeReward.badgeRewardSchedules ?? [])
  const renewableOptions = getRenewableOptions(selectedBadgeRewardType)

  const searchRewardPrograms = useTypedApiClient({
    path: '/admin/rewards-programs/search',
    method: 'get',
  })

  const rewardProgramsQuery = useQuery<RewardsProgram[], ApiError>({
    queryKey: ['rewardPrograms', debouncedRewardProgramSearchQuery],
    queryFn: async () => {
      if (debouncedRewardProgramSearchQuery.length >= 2) {
        return (
          await searchRewardPrograms({
            query: debouncedRewardProgramSearchQuery,
          })
        ).data
      }

      return []
    },
  })

  const filteredRewardPrograms = rewardProgramsQuery.data?.filter(
    (rewardProgram) => rewardProgram.type !== 'Global' && rewardProgram.active
  )

  const putBadgeRewardMutation = usePutBadgeReward({
    id: Number(badgeReward.id),
    onSuccess: () => {
      setSnackbarMessage(`Badge Reward has been updated`)
      toggleModalIsOpen()
    },
    onError: (error: ApiError) => {
      const apiErrorMessage =
        typeof error.data === 'string'
          ? error.data
          : (error.data as { message?: string } | undefined)?.message

      setSnackbarMessage(
        apiErrorMessage ?? `Error updating Badge Reward`,
        'error'
      )
    },
  })

  // These fields aren't tracked by the yup schema, so they must be validated
  // outside of handleSubmit or their errors get silently skipped when other fields are also invalid
  const validateManualFields = () => {
    let isValid = true

    if (
      badgeRewardTypeHasAccessToControl(selectedBadgeRewardType, 'Count') &&
      count < 1
    ) {
      setCountValidationError('Count must be at least 1')
      isValid = false
    } else {
      setCountValidationError(null)
    }

    if (
      locationSweepstakeEntriesAwarded > 0 &&
      selectedLocationSweepstakeContestId === 0 &&
      selectedLocationSweepstakeGroupId.length === 0
    ) {
      setLocationSweepstakeSelectionError(
        'Must select either a regular location sweepstake or a multi draw sweepstake'
      )
      isValid = false
    } else {
      setLocationSweepstakeSelectionError(null)
    }

    if (locationPointsAwarded > 0 && !selectedRewardProgram) {
      setRewardProgramSelectionError('Must select a rewards program')
      isValid = false
    } else {
      setRewardProgramSelectionError(null)
    }

    return isValid
  }

  const submitBadgeReward = handleSubmit((data) => {
    const badgeRewardSchedules: BadgeRewardSchedule[] = []
    data.schedules?.forEach((schedule: BadgeRewardSchedule) => {
      if (schedule.opening && schedule.closing) {
        badgeRewardSchedules.push({
          ...schedule,
        })
      }
    })

    putBadgeRewardMutation.mutate({
      ...badgeReward,
      name: data.name,
      description: String(data.description),
      type: selectedBadgeRewardType,
      subtype: data.subtype as BadgeSubtype,
      startDate:
        data.startDate && data.startTime
          ? formatISO(parse(data.startTime, 'hh:mm a', data.startDate))
          : null,
      endDate:
        data.endDate && data.endTime
          ? formatISO(parse(data.endTime, 'hh:mm a', data.endDate))
          : null,
      locationPointsAwarded,
      locationSweepstakeEntriesAwarded,
      badgeRewardSchedules,
      renewableType: selectedBadgeRewardRenewable,
      dependentBadgeRewardId: Number(data.dependsOnBadgeRewardId) || 0,
      count,
      locationSweepstakeContestId:
        locationSweepstakeEntriesAwarded > 0 &&
        selectedLocationSweepstakeContestId > 0
          ? selectedLocationSweepstakeContestId
          : null,
      locationSweepstakeGroupId:
        locationSweepstakeEntriesAwarded > 0 &&
        selectedLocationSweepstakeGroupId.length > 0
          ? selectedLocationSweepstakeGroupId
          : null,
      licensedEstablishmentIds:
        data.subtype === 'Location' &&
        Array.isArray(data.licensedEstablishmentIds) &&
        data.licensedEstablishmentIds.length > 0
          ? data.licensedEstablishmentIds.filter(
              (id: number | undefined): id is number => typeof id === 'number'
            )
          : null,
      corporateAccountId:
        data.subtype === 'CorporateAccount'
          ? Number(data.corporateAccountId)
          : null,
      organizationId:
        data.subtype === 'Organization' ? Number(data.organizationId) : null,
      rewardsProgramId: selectedRewardProgram
        ? Number(selectedRewardProgram.id)
        : null,
      limitCheckins,
    })
  })

  const handleSubmitWrapper = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateManualFields()) {
      return
    }

    void submitBadgeReward(event)
  }

  const handleTypeChange = (e: SelectChangeEvent) => {
    const nextType = e.target.value as BadgeRewardType

    setSelectedBadgeRewardType(nextType)
    setSelectedBadgeRewardRenewable((currentRenewable) =>
      getValidRenewableValue(nextType, currentRenewable)
    )
  }

  const handleRenewableChange = (e: SelectChangeEvent) => {
    setSelectedBadgeRewardRenewable(e.target.value as BadgeRewardRenewableType)
  }

  const handleDependentBadgeRewardChange = (e: SelectChangeEvent) => {
    setselectedBadgeRewardDependent(Number(e.target.value))
  }

  const handleRegularLocationSweepstakeChange = (e: SelectChangeEvent) => {
    const nextContestId = Number(e.target.value)
    setSelectedLocationSweepstakeContestId(nextContestId)
    if (nextContestId > 0) {
      setSelectedLocationSweepstakeGroupId('')
    }
    if (locationSweepstakeSelectionError) {
      setLocationSweepstakeSelectionError(null)
    }
  }

  const handleGroupedLocationSweepstakeChange = (e: SelectChangeEvent) => {
    const nextGroupId = String(e.target.value)
    setSelectedLocationSweepstakeGroupId(nextGroupId)
    if (nextGroupId.length > 0) {
      setSelectedLocationSweepstakeContestId(0)
    }
    if (locationSweepstakeSelectionError) {
      setLocationSweepstakeSelectionError(null)
    }
  }

  const handleRewardProgramClick = (rewardProgram: RewardsProgram) => {
    setSelectedRewardProgram(rewardProgram)
    setValue('rewardsProgramId', Number(rewardProgram.id), {
      shouldDirty: true,
    })
    setRewardProgramSelectionError(null)
  }

  const onCloseRewardProgram = () => {
    setRewardProgramSearchQuery('')
    setSelectedRewardProgram(undefined)
    setValue('rewardsProgramId', null, { shouldDirty: true })
  }

  const defaultStartDate = badgeReward.startDate
    ? parseISO(badgeReward.startDate)
    : null
  const defaultEndDate = badgeReward.endDate
    ? parseISO(badgeReward.endDate)
    : null

  const hasBadgeStarted = Boolean(
    defaultStartDate && defaultStartDate <= new Date()
  )

  return (
    <ModalComponent
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <ModalFullHeaderComponent
            title={badgeReward.name}
            toggleIsOpen={toggleModalIsOpen}
          />
          <ModalFullBodyComponent heightModal={heightModal}>
            <Grid container>
              <Grid container>
                {Object.keys(errors).length > 0 && (
                  <Grid item xs={12} pb={4}>
                    {Object.keys(errors).length > 0 && (
                      <Alert severity="error">
                        Please correct errors to continue
                      </Alert>
                    )}{' '}
                  </Grid>
                )}

                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="name"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Name
                  </InputLabel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    error={isPresent(errors.name)}
                    placeholder="Enter Badge Reward Name"
                    defaultValue={badgeReward?.name}
                    {...register('name')}
                  />
                  {errors.name?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.name.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="type"
                    required
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Type
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Select
                    {...register('type', { required: true })}
                    inputProps={{ id: 'rewardType' }}
                    error={isPresent(errors.type)}
                    value={selectedBadgeRewardType ?? badgeReward?.type}
                    sx={{ width: '100%', maxWidth: '520px' }}
                    onChange={handleTypeChange}
                  >
                    <MenuItem value="LocationCheckIn">
                      Location Check-In
                    </MenuItem>
                    <MenuItem value="LocationCheckInStreak">
                      Location Check-In Streak
                    </MenuItem>
                    <MenuItem value="UniqueLocationVisits">
                      Unique Location Visits
                    </MenuItem>
                  </Select>
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="subtype"
                    required
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Subtype
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocationBadgeTypeSelect
                    name="subtype"
                    data-testid="LocationBadgeTypeSelect"
                    types={['Location', 'CorporateAccount', 'Organization']}
                    disabled={false}
                  />
                  {errors.subtype?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.subtype.message}
                    />
                  )}
                </Grid>
              </Grid>

              {subtype === 'Location' && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} />
                  <Grid item xs={12} md={6}>
                    <LocationSelect
                      required
                      showLabel
                      name="licensedEstablishmentIds"
                      data-testid="LocationSelect"
                      defaultValue={badgeReward.licensedEstablishmentIds ?? []}
                      defaultSelected={defaultSelectedLocations}
                    />
                    {errors.licensedEstablishmentIds?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.licensedEstablishmentIds.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {subtype === 'CorporateAccount' && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} />
                  <Grid item xs={12} md={6}>
                    <SelectCorporateAccount
                      data-testid="SelectCorporateAccount"
                      required
                      error={isPresent(errors.corporateAccountId)}
                      {...register('corporateAccountId')}
                      defaultValue={badgeReward.corporateAccountId}
                      activityIndicatorMaxWidth="520px"
                      label="Corporate Account"
                      fullWidth
                      corporateAccountIdKey="corporateAccountId"
                      placeholder="Select a Corporate Account"
                    />
                    {errors.corporateAccountId?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.corporateAccountId.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {subtype === 'Organization' && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} />
                  <Grid item xs={12} md={6}>
                    <SelectFromAllOrganizations
                      {...register('organizationId')}
                      defaultValue={badgeReward.organizationId}
                      activityIndicatorMaxWidth="520px"
                      required
                      error={isPresent(errors.organizationId)}
                      label="Organization"
                      fullWidth
                      organizationIdKey="organizationId"
                      placeholder="Select an Organization"
                    />
                    {errors.organizationId?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.organizationId.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="description"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    required
                    error={isPresent(errors.description)}
                    placeholder="Describe the badge reward"
                    {...register('description')}
                    defaultValue={badgeReward.description}
                    sx={{ width: '100%' }}
                  />
                  <Grid item xs={12} md={6}>
                    <Typography variant="body-3">500 characters max</Typography>
                  </Grid>
                  {errors.description?.message && (
                    <FormFieldErrorMessageComponent
                      message={errors.description.message}
                    />
                  )}
                </Grid>
              </Grid>

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'StartDateTime'
              ) && (
                <Grid container pt={2}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="startDate"
                      required
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Start Date/Time
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableDateFieldComponent
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startDate),
                        },
                      }}
                      defaultValue={defaultStartDate}
                      {...register('startDate')}
                    />
                    {errors.startDate?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startDate.message}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={0.2}></Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableTimeFieldComponent
                      defaultValue={defaultStartDate as string | null}
                      {...register('startTime')}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.startTime),
                        },
                      }}
                    />
                    {errors.startTime?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.startTime.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'EndDateTime'
              ) && (
                <Grid container pt={2}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="endDate"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      End Date/Time
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableDateFieldComponent
                      defaultValue={defaultEndDate}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <EventIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endDate),
                        },
                      }}
                      {...register('endDate')}
                    />
                    {errors.endDate?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.endDate.message}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={0.2}></Grid>
                  <Grid item xs={12} md={2.45}>
                    <HookableTimeFieldComponent
                      {...register('endTime')}
                      defaultValue={defaultEndDate as string | null}
                      slotProps={{
                        textField: {
                          required: true,
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeIconComponent />
                              </InputAdornment>
                            ),
                          },
                          error: isPresent(errors.endTime),
                        },
                      }}
                      sx={{ width: '100%' }}
                    />
                    {errors.endTime?.message && (
                      <FormFieldErrorMessageComponent
                        message={errors.endTime.message}
                      />
                    )}
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Renewable'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="renewable"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Renewable
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      {...register('renewable', { required: true })}
                      inputProps={{ id: 'renewableType' }}
                      error={isPresent(errors.renewable)}
                      value={selectedBadgeRewardRenewable}
                      sx={{ width: '100%', maxWidth: '520px' }}
                      onChange={handleRenewableChange}
                    >
                      {renewableOptions.map((renewableOption) => (
                        <MenuItem key={renewableOption} value={renewableOption}>
                          {renewableOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'DependentOnBadge'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="dependentBadgeRewardId"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Dependent on badge reward
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      {...register('dependsOnBadgeRewardId', {
                        required: false,
                      })}
                      inputProps={{ id: 'dependsOnBadgeRewardId' }}
                      error={isPresent(errors.dependsOnBadgeRewardId)}
                      value={String(selectedBadgeRewardDependent)}
                      sx={{ width: '100%', maxWidth: '520px' }}
                      onChange={handleDependentBadgeRewardChange}
                    >
                      <MenuItem value={0}>None</MenuItem>
                      {Object.entries(badgeRewards).map(([id, name]) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'LocationPointsToAward'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }} columnSpacing={2}>
                    <InputLabel
                      htmlFor="locationPointsAwarded"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Location points to award on Badge earned
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2.45}
                    display={'flex'}
                    alignItems={'flex-start'}
                  >
                    <Counter
                      amount={locationPointsAwarded}
                      setAmount={setLocationPointsAwarded}
                      minimumAmount={0}
                      disabled={hasBadgeStarted}
                    />
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'RewardsProgramId'
              ) &&
                locationPointsAwarded > 0 && (
                  <Grid container pt={4}>
                    <Grid item xs={12} md={6} pt={{ md: 2 }}>
                      <InputLabel
                        required
                        htmlFor="rewardsProgramId"
                        sx={{ fontSize: '16px', fontWeight: '100' }}
                      >
                        Rewards Program
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body-2"
                        color={'rgba(44, 44, 39, 0.68)'}
                      >
                        Make sure to use a minimum of at least 2 characters per
                        word when searching.
                      </Typography>
                      <SearchField
                        placeholder="Start typing to search"
                        value={rewardProgramSearchQuery}
                        onChange={(e) =>
                          setRewardProgramSearchQuery(e.target.value)
                        }
                        fullWidth
                        sx={{ my: '16px' }}
                      />
                      {!selectedRewardProgram &&
                        !!filteredRewardPrograms?.length && (
                          <Paper variant="outlined" sx={{ mt: '7px' }}>
                            {filteredRewardPrograms.map((rewardProgram) => {
                              let resultString = `${rewardProgram.name} - ${rewardProgram.id}`
                              resultString = resultString.replace(
                                rewardProgramSearchQuery,
                                `<strong>${rewardProgramSearchQuery}</strong>`
                              )

                              return (
                                <MenuItem
                                  key={rewardProgram.id}
                                  value={rewardProgram.id}
                                  onClick={() =>
                                    handleRewardProgramClick(rewardProgram)
                                  }
                                >
                                  <p
                                    dangerouslySetInnerHTML={{
                                      __html: resultString,
                                    }}
                                  />
                                </MenuItem>
                              )
                            })}
                          </Paper>
                        )}
                      {selectedRewardProgram && (
                        <Box
                          sx={{
                            backgroundColor: colorPrimitives.lightGray,
                            p: '12px',
                          }}
                          maxWidth={'520px'}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex flex-row items-center">
                              <SvgIcon
                                sx={{
                                  color: colors.text.primary,
                                  transform: 'translate(0, -2px)',
                                  height: '40px',
                                  width: '40px',
                                  marginRight: '10px',
                                }}
                              >
                                {RewardProgramIconComponent ? (
                                  <RewardProgramIconComponent />
                                ) : null}
                              </SvgIcon>
                              <div className="flex flex-col">
                                <Typography variant="body-1">
                                  {selectedRewardProgram.name}
                                </Typography>
                                <Typography
                                  variant="body-1"
                                  color="text.secondary"
                                >
                                  {selectedRewardProgram.id}
                                </Typography>
                              </div>
                            </div>
                            <Box
                              onClick={onCloseRewardProgram}
                              sx={{
                                '&:hover': {
                                  cursor: 'pointer',
                                },
                              }}
                            >
                              <SvgIcon
                                sx={{
                                  transform: 'translate(0, -2px)',
                                  height: '24px',
                                  width: '24px',
                                }}
                              >
                                {CloseIconComponent ? (
                                  <CloseIconComponent />
                                ) : null}
                              </SvgIcon>
                            </Box>
                          </div>
                        </Box>
                      )}
                      <input type="hidden" {...register('rewardsProgramId')} />
                      {rewardProgramSelectionError && (
                        <FormFieldErrorMessageComponent
                          message={rewardProgramSelectionError}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'LocationSweepstakeEntriesToAward'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }} columnSpacing={2}>
                    <InputLabel
                      htmlFor="locationSweepstakeEntriesAwarded"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Location Sweepstake Entries to award
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2.45}
                    display={'flex'}
                    alignItems={'flex-start'}
                  >
                    <Counter
                      amount={locationSweepstakeEntriesAwarded}
                      setAmount={setLocationSweepstakeEntriesAwarded}
                      minimumAmount={0}
                      disabled={hasBadgeStarted}
                    />
                  </Grid>

                  {locationSweepstakeEntriesAwarded > 0 && (
                    <Grid container pt={3} spacing={2}>
                      <Grid item xs={12} md={6} pt={{ md: 2 }}>
                        <InputLabel
                          htmlFor="locationSweepstakeContestId"
                          sx={{ fontSize: '16px', fontWeight: '100' }}
                        >
                          Regular Location Sweepstake
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Select
                          id="locationSweepstakeContestId"
                          value={String(selectedLocationSweepstakeContestId)}
                          onChange={handleRegularLocationSweepstakeChange}
                          fullWidth
                          disabled={
                            selectedLocationSweepstakeGroupId.length > 0
                          }
                        >
                          <MenuItem value="0">None</MenuItem>
                          {regularLocationSweepstakes.map((sweepstake) => (
                            <MenuItem
                              key={sweepstake.id}
                              value={String(sweepstake.id)}
                            >
                              {sweepstake.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      <Grid item xs={12} md={6} pt={{ md: 2 }}>
                        <InputLabel
                          htmlFor="locationSweepstakeGroupId"
                          sx={{ fontSize: '16px', fontWeight: '100' }}
                        >
                          Multi Draw Location Sweepstake
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Select
                          id="locationSweepstakeGroupId"
                          value={selectedLocationSweepstakeGroupId}
                          onChange={handleGroupedLocationSweepstakeChange}
                          fullWidth
                          displayEmpty
                          disabled={selectedLocationSweepstakeContestId > 0}
                        >
                          <MenuItem value="">None</MenuItem>
                          {groupedLocationSweepstakes.map((sweepstake) => (
                            <MenuItem
                              key={sweepstake.groupId ?? sweepstake.id}
                              value={sweepstake.groupId ?? ''}
                            >
                              {sweepstake.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      {locationSweepstakeSelectionError && (
                        <Grid item xs={12}>
                          <FormFieldErrorMessageComponent
                            message={locationSweepstakeSelectionError}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Count'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }} columnSpacing={2}>
                    <InputLabel
                      htmlFor="count"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      {getBadgeRewardTypeName(selectedBadgeRewardType)} Count
                    </InputLabel>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2.45}
                    display={'flex'}
                    alignItems={'flex-start'}
                  >
                    <Counter
                      amount={count}
                      setAmount={setCount}
                      minimumAmount={0}
                    />
                  </Grid>
                  {countValidationError && (
                    <Grid item xs={12} md={6}>
                      <FormFieldErrorMessageComponent
                        message={countValidationError}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'LimitCheckins'
              ) && (
                <Grid container pt={4}>
                  <Grid item xs={12} md={6} pt={{ md: 2 }}>
                    <InputLabel
                      htmlFor="LimitCheckins"
                      sx={{ fontSize: '16px', fontWeight: '100' }}
                    >
                      Limit to 1 Check-in Per Day?
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      id="LimitCheckins"
                      value={limitCheckins ? 'true' : 'false'}
                      onChange={(e) =>
                        setLimitCheckins(e.target.value === 'true')
                      }
                      fullWidth
                      displayEmpty
                    >
                      <MenuItem value="false">No</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              )}

              {badgeRewardTypeHasAccessToControl(
                selectedBadgeRewardType,
                'Schedule'
              ) && (
                <Grid className="pt-8 w-[100%]">
                  <Typography variant="subtitle-1" component={'p'}>
                    Badge Schedule
                  </Typography>
                  <Typography variant="body-3">
                    {`Select the days you'd like the badge to be available to patrons`}
                  </Typography>
                  <Grid item spacing={2} mt="20px">
                    {daysToDisplay.map((day, index) => {
                      return (
                        <Box
                          key={day.dayName}
                          style={{ height: '85px' }}
                          height={'85'}
                          className="flex flex-col justify-center"
                        >
                          <ScheduleRowComponent
                            day={day.dayName ?? ''}
                            index={index}
                            key={day.dayName}
                            defaultValues={{
                              opening:
                                badgeReward?.badgeRewardSchedules?.find(
                                  (schedule: BadgeRewardSchedule) =>
                                    schedule.dayName === day.dayName
                                )?.opening ?? '',
                              closing:
                                badgeReward?.badgeRewardSchedules?.find(
                                  (schedule: BadgeRewardSchedule) =>
                                    schedule.dayName === day.dayName
                                )?.closing ?? '',
                              patronCheckInPointBonusMultiplier: 0,
                            }}
                            openLabel="Enabled"
                            closedLabel="Disabled"
                          />
                        </Box>
                      )
                    })}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </ModalFullBodyComponent>
          <ModalFullFooterComponent>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButtonComponent
              active={putBadgeRewardMutation.isPending}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButtonComponent>
          </ModalFullFooterComponent>
        </form>
      </FormProvider>
    </ModalComponent>
  )
}
